import React from "react";
import { NumericFormat } from "react-number-format";
import { BsGripVertical } from "react-icons/bs";
import { HiLockClosed } from "react-icons/hi";
import { BsTrash, BsPencil } from "react-icons/bs";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import PopoverContainer from "components/common/Popover/PopoverContainer";
import { capitalizeWords, parseBooleanVal } from "Utils/utils";

export const PARTICIPANT_DOC_GRID_CLASS =
  "grid-cols-[20px_minmax(140px,1fr)_100px_100px_52px_92px_56px]";

const DocumentRow = ({
  item,
  complianceType,
  lastErrorLocation,
  onSwitchChange,
  onScoreChange,
  onLastErrorChange,
  icon = "grip",
  dragHandleProps,
  onDelete,
  deleteLoading = false,
  onEdit,
  disabled: rowDisabled = false,
  uploadSubject = "workers",
  showDateRequirements = false,
  onDateRequirementChange,
  mandatorySubtitle = "Mandatory for all staff",
}) => {
  const fieldId = `switches[${item.name}]['${
    item.id ?? `tpl-${item.sp_form_template_id}`
  }']`;
  const hasError =
    (lastErrorLocation || []).findIndex((x) => x === fieldId) !== -1;
  const displayName = capitalizeWords(item.name || "");

  const subtitle =
    item.description ||
    (parseBooleanVal(item.is_compliance) ? mandatorySubtitle : null);

  const isForm = icon === "lock" || icon === "form";
  const showActions = !isForm && item.uuid && !rowDisabled;
  const showDelete = showActions && onDelete;
  const showEdit = showActions && onEdit;
  const deleteDisabled = parseBooleanVal(item.is_compliance) || deleteLoading || rowDisabled;
  const switchDisabled = rowDisabled;
  const weightDisabled = rowDisabled || !item.is_compliance;
  const useParticipantLayout = showDateRequirements && !isForm;

  const mandatoryToggle = (
    <div className="flex items-center gap-2 flex-shrink-0">
      <PopoverContainer
        isHover={true}
        placement="left"
        itemComponent={() => (
          <div className="ml-1 mr-1 rounded border bg-white text-sm min-w-[260px] max-w-[350px]">
            <div className="rounded flex justify-between gap-2 items-center py-0.5 px-3 background-gradient border-gradient text-white">
              {`Click here to set the ${complianceType} as ${
                !item.is_compliance ? "mandatory" : "non-mandatory."
              }`}
            </div>
          </div>
        )}
      >
        <span className={switchDisabled ? "pointer-events-none opacity-60" : ""}>
          <SwitchWithIcon
            onChange={(value) => {
              if (switchDisabled) return;
              onSwitchChange(item, value, "switch");
              const prevObj = [...(lastErrorLocation || [])];
              const idx = prevObj.findIndex((x) => x === fieldId);
              if (value) {
                if (idx === -1) prevObj.push(fieldId);
              } else {
                if (idx !== -1) prevObj.splice(idx, 1);
              }
              onLastErrorChange(prevObj);
            }}
            checked={item.is_compliance}
            disabled={switchDisabled}
          />
        </span>
      </PopoverContainer>
    </div>
  );

  const percentageField = (
    <NumericFormat
      disabled={weightDisabled}
      allowNegative={false}
      value={item.score}
      id={fieldId}
      min={0}
      minLength={1}
      suffix="%"
      type="text"
      max={100}
      maxLength={4}
      className={`${
        (parseBooleanVal(item.is_compliance) && Number(item.score) === 0) ||
        hasError
          ? "errorBorder"
          : ""
      } focus:bg-[#4b6bb710] px-3 py-2 border shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:opacity-50`}
      valueIsNumericString={true}
      decimalScale={0}
      allowLeadingZeros={false}
      onValueChange={(e) => {
        let floatValue = parseFloat(
          e.floatValue === undefined ? 0 : e.floatValue
        );
        floatValue = Math.round(floatValue);
        if (floatValue > 100) floatValue = 100;
        if (floatValue < 0) floatValue = 0;
        onScoreChange(item, floatValue);
        const prevObj = [...(lastErrorLocation || [])];
        const idx = prevObj.findIndex((x) => x === fieldId);
        if (idx === -1) prevObj.push(fieldId);
        onLastErrorChange(prevObj);
      }}
    />
  );

  const percentageInput = (
    <div className={`flex flex-col gap-1 ${useParticipantLayout ? "w-full" : "min-w-[100px]"}`}>
      <div className="flex items-center gap-2">
        {!useParticipantLayout && (
          <span className="text-xs font-medium text-gray-500 uppercase">Percentage</span>
        )}
        {percentageField}
      </div>
    </div>
  );

  const dateRequirementToggle = (field, label, compact = false) => (
    <div
      className={
        compact
          ? "flex justify-center"
          : "flex flex-col items-center justify-center gap-1.5 min-w-[118px] px-1"
      }
    >
      {!compact && (
        <span className="text-xs font-medium text-gray-600 text-center leading-tight">
          {label}
        </span>
      )}
      <SwitchWithIcon
        checked={!!item[field]}
        disabled={rowDisabled}
        onChange={(value) => {
          if (rowDisabled) return;
          if (onDateRequirementChange) {
            onDateRequirementChange(item, field, value);
          } else {
            onSwitchChange(item, value, field);
          }
        }}
      />
    </div>
  );

  const actionButtons = (showEdit || showDelete) && (
    <div className="flex items-center justify-end gap-1">
      {showEdit && (
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="p-0.5 rounded text-indigo-600 hover:bg-indigo-50"
          title="Edit document"
          aria-label="Edit document"
        >
          <BsPencil className="w-3 h-3" />
        </button>
      )}
      {showDelete && (
        <button
          type="button"
          onClick={() => !deleteDisabled && onDelete(item)}
          disabled={deleteDisabled}
          className="p-0.5 rounded text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title={deleteDisabled && item.is_compliance ? "Uncheck Mandatory first to remove" : "Remove document from compliance"}
          aria-label={deleteDisabled && item.is_compliance ? "Uncheck Mandatory first to remove" : "Remove document from compliance"}
        >
          <BsTrash className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  if (useParticipantLayout) {
    return (
      <div
        className={`text-sm border-b border-gray-100 last:border-b-0 ${
          rowDisabled
            ? "bg-gray-50/80 opacity-75 pointer-events-none"
            : "bg-white hover:bg-gray-50/50"
        } transition-colors`}
      >
        <div
          className={`hidden lg:grid ${PARTICIPANT_DOC_GRID_CLASS} gap-x-4 items-center px-4 py-3`}
        >
          <div
            className={`flex-shrink-0 ${
              rowDisabled ? "text-gray-300 cursor-default" : "text-gray-400 cursor-grab active:cursor-grabbing"
            }`}
            {...(dragHandleProps?.attributes || {})}
            {...(dragHandleProps?.listeners || {})}
          >
            <BsGripVertical className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{displayName}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
            )}
          </div>
          {dateRequirementToggle("require_issue_date", "Require issue date", true)}
          {dateRequirementToggle("require_expiry_date", "Require expiry date", true)}
          <div className="flex justify-center">{mandatoryToggle}</div>
          <div>{percentageInput}</div>
          {actionButtons}
        </div>

        <div className="flex flex-col gap-3 px-4 py-4 lg:hidden">
          <div className="flex items-center gap-3">
            <div
              className={`flex-shrink-0 ${
                rowDisabled ? "text-gray-300 cursor-default" : "text-gray-400 cursor-grab active:cursor-grabbing"
              }`}
              {...(dragHandleProps?.attributes || {})}
              {...(dragHandleProps?.listeners || {})}
            >
              <BsGripVertical className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900">{displayName}</p>
              {subtitle && (
                <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
              )}
            </div>
            {actionButtons}
          </div>
          <div className="flex flex-wrap items-center gap-4 justify-between">
            {dateRequirementToggle("require_issue_date", "Require issue date")}
            {dateRequirementToggle("require_expiry_date", "Require expiry date")}
            <div className="flex items-center gap-3">
              {mandatoryToggle}
              <span className="text-gray-300">|</span>
              {percentageInput}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const requireIssueDateTooltip = `When enabled, ${uploadSubject} cannot submit this document without an issue date.`;
  const requireExpiryDateTooltip = `When enabled, ${uploadSubject} cannot submit this document without an expiry date.`;

  const dateRequirementTooltipContent = (message) => () => (
    <div className="rounded text-sm min-w-[200px] max-w-[300px]">
      <div className="rounded py-1.5 px-3 background-gradient border-gradient text-white">
        {message}
      </div>
    </div>
  );

  return (
    <div className={`flex items-center gap-4 text-sm border-b border-gray-100 last:border-b-0 ${rowDisabled ? "bg-gray-50/80 opacity-75 pointer-events-none" : "bg-white hover:bg-gray-50/50"} transition-colors ${isForm ? "py-4 px-5 gap-5" : useParticipantLayout ? "py-4 px-4" : "py-3 px-4"}`}>
      {!isForm || icon === "lock" ? (
        <div
          className={`flex-shrink-0 ${
            isForm ? "text-gray-400" : rowDisabled ? "text-gray-300 cursor-default" : "text-gray-400 cursor-grab active:cursor-grabbing"
          }`}
          {...(dragHandleProps?.attributes || {})}
          {...(dragHandleProps?.listeners || {})}
        >
          {icon === "lock" ? (
            <HiLockClosed className="w-5 h-5" />
          ) : (
            <BsGripVertical className="w-5 h-5" />
          )}
        </div>
      ) : null}
      <div className={`min-w-0 ${useParticipantLayout ? "flex-1 max-w-[220px]" : "flex-1"}`}>
        <p className="text-sm font-medium text-gray-900">{displayName}</p>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className={`flex items-center flex-shrink-0 ml-auto ${isForm ? "gap-8" : "gap-6"}`}>
        {!isForm && (
          <>
            <div className="flex flex-col gap-1.5 flex-shrink-0 min-w-[158px]">
              <PopoverContainer
                isHover={true}
                placement="top"
                itemComponent={dateRequirementTooltipContent(requireIssueDateTooltip)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600 max-w-[100px] leading-tight">
                    Require issue date
                  </span>
                  <span className={rowDisabled ? "pointer-events-none opacity-60" : ""}>
                    {dateRequirementToggle("require_issue_date", "Require issue date", true)}
                  </span>
                </div>
              </PopoverContainer>
              <PopoverContainer
                isHover={true}
                placement="top"
                itemComponent={dateRequirementTooltipContent(requireExpiryDateTooltip)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600 max-w-[100px] leading-tight">
                    Require expiry date
                  </span>
                  <span className={rowDisabled ? "pointer-events-none opacity-60" : ""}>
                    {dateRequirementToggle("require_expiry_date", "Require expiry date", true)}
                  </span>
                </div>
              </PopoverContainer>
            </div>
            <span className="text-gray-300 flex-shrink-0">|</span>
          </>
        )}
        {mandatoryToggle}
        <span className={`text-gray-300 flex-shrink-0 ${isForm ? "mx-1" : ""}`}>|</span>
        {percentageInput}
        {actionButtons}
      </div>
    </div>
  );
};

export default DocumentRow;
